import React, { Component } from "react";
import axios from "axios";
import { connect } from "react-redux";
import * as XLSX from "xlsx";
import { Alert, Modal, Table, Tag, Row, Col } from "antd";
import ImportadorExcelGenerico from "../../../../Components/ImportadorExcelGenerico";
import { SelectItem, ButtonItem } from "../../../../Components/Items";

const normalizar = (t) =>
  String(t ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const nombreCompleto = (u) =>
  [u.nombre, u.apellidoPaterno, u.apellidoMaterno].filter(Boolean).join(" ");

const COLUMNAS_FIJAS = [
  "Nombre",
  "Apellido paterno",
  "Apellido materno",
  "Telefono",
  "Correo",
  "Descripcion del lead",
  "Valor",
  "Origen",
  "Vendedor",
];

const fechaExcel = (v) => {
  if (v === undefined || v === null || v === "") return { ok: true, valor: null };
  const dos = (n) => String(n).padStart(2, "0");
  if (typeof v === "number") {
    const d = new Date(Math.round((v - 25569) * 86400 * 1000));
    if (isNaN(d.getTime())) return { ok: false };
    return {
      ok: true,
      valor: `${dos(d.getUTCDate())}/${dos(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`,
    };
  }
  const m = String(v).trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return { ok: true, valor: `${dos(m[1])}/${dos(m[2])}/${m[3]}` };
  return { ok: false };
};

const booleanoExcel = (v) => {
  const t = normalizar(v);
  if (["si", "true", "1", "verdadero", "x"].includes(t)) return { ok: true, valor: true };
  if (["no", "false", "0", "falso"].includes(t)) return { ok: true, valor: false };
  return { ok: false };
};

const construirPlantilla = (catalogos, destino) => {
  const campos = catalogos?.campos || [];
  const embudos = destino?.embudo ? [destino.embudo] : catalogos?.embudos || [];
  const wb = XLSX.utils.book_new();

  const ejemplo = {
    Nombre: "Juan",
    "Apellido paterno": "Pérez",
    "Apellido materno": "López",
    Telefono: "5512345678",
    Correo: "juan.perez@ejemplo.com",
    "Descripcion del lead": "Interesado en cotización de 50 piezas",
    Valor: 15000,
    Origen: "Facebook",
    Vendedor: embudos[0]?.usuarios?.[0]?.correo || "",
  };
  campos.forEach((c) => {
    ejemplo[c.nombre] =
      c.tipo === "number"
        ? 10
        : c.tipo === "boolean"
          ? "Si"
          : c.tipo === "date"
            ? "15/01/2026"
            : c.tipo === "select"
              ? c.opciones?.[0] || ""
              : "";
  });

  const encabezados = [...COLUMNAS_FIJAS, ...campos.map((c) => c.nombre)];
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet([ejemplo], { header: encabezados }),
    "Leads",
  );

  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet([
      { Dato: "Embudo", Valor: destino?.embudo?.nombreEmbudo || "Sin elegir (la hoja Vendedores trae todos los embudos)" },
      { Dato: "Etapa", Valor: destino?.etapa?.nombre || "Sin elegir" },
      {
        Dato: "Importante",
        Valor: "Los leads de este archivo entran a ese embudo y etapa. Vendedor solo acepta a quienes aparecen en la hoja Vendedores.",
      },
    ]),
    "Destino",
  );

  const vendedores = [];
  embudos.forEach((e) =>
    (e.usuarios || []).forEach((u) =>
      vendedores.push({
        Embudo: e.nombreEmbudo,
        Vendedor: nombreCompleto(u),
        Correo: u.correo,
      }),
    ),
  );
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(vendedores), "Vendedores");

  if (campos.length) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        campos.map((c) => ({
          Campo: c.nombre,
          Tipo:
            { text: "Texto", number: "Número", date: "Fecha (dd/mm/aaaa)", boolean: "Si / No", select: "Lista" }[c.tipo] || c.tipo,
          Opciones: (c.opciones || []).join(", "),
        })),
      ),
      "Campos personalizados",
    );
  }
  return wb;
};

const prepararCatalogos = (data) => ({
  ...data,
  embudos: data.embudos || [],
  campos: data.campos || [],
});

const indicesVendedores = (usuarios) => {
  const porCorreo = new Map();
  const porNombre = new Map();
  usuarios.forEach((u) => {
    if (u.correo) porCorreo.set(normalizar(u.correo), u);
    const k = normalizar(nombreCompleto(u));
    porNombre.set(k, porNombre.has(k) ? null : u);
  });
  return { porCorreo, porNombre };
};

const construirValidador = (obtenerEmbudoId) => (fila, cat, _indice, contexto) => {
  const errores = [];
  const get = (col) => (fila[col] === undefined || fila[col] === null ? "" : String(fila[col]).trim());

  const nombre = get("Nombre");
  if (!nombre) errores.push({ campo: "Nombre", motivo: "está vacío" });
  const apellidoPaterno = get("Apellido paterno");
  if (!apellidoPaterno) errores.push({ campo: "Apellido paterno", motivo: "está vacío" });
  const descripcion = get("Descripcion del lead");
  if (!descripcion) errores.push({ campo: "Descripcion del lead", motivo: "está vacía" });

  const numero = get("Telefono");
  if (numero && numero.replace(/\D/g, "").length < 10)
    errores.push({ campo: "Telefono", motivo: "debe tener al menos 10 dígitos" });

  const correo = get("Correo");
  if (correo && !/^\S+@\S+\.\S+$/.test(correo))
    errores.push({ campo: "Correo", motivo: "no es un correo válido" });

  const valorTexto = get("Valor");
  let valor = null;
  if (valorTexto) {
    valor = Number(String(valorTexto).replace(/[$,\s]/g, ""));
    if (!Number.isFinite(valor) || valor < 0)
      errores.push({ campo: "Valor", motivo: "debe ser un número" });
  }

  let usuarioId = null;
  let vendedorNombre = "";
  const vendedorTexto = get("Vendedor");
  if (vendedorTexto) {
    if (!contexto.vendedores) {
      const embudo = cat.embudos.find((e) => e._id === obtenerEmbudoId());
      contexto.vendedores = indicesVendedores(embudo?.usuarios || []);
    }
    const u =
      contexto.vendedores.porCorreo.get(normalizar(vendedorTexto)) ||
      contexto.vendedores.porNombre.get(normalizar(vendedorTexto));
    if (u) {
      usuarioId = u._id;
      vendedorNombre = nombreCompleto(u);
    } else if (contexto.vendedores.porNombre.get(normalizar(vendedorTexto)) === null) {
      errores.push({ campo: "Vendedor", motivo: `"${vendedorTexto}" coincide con varios usuarios (use el correo)` });
    } else {
      const nombreEmbudo = cat.embudos.find((e) => e._id === obtenerEmbudoId())?.nombreEmbudo;
      errores.push({ campo: "Vendedor", motivo: `"${vendedorTexto}" no tiene acceso al embudo "${nombreEmbudo}"` });
    }
  }

  const camposPersonalizados = {};
  cat.campos.forEach((campo) => {
    const bruto = fila[campo.nombre];
    if (bruto === undefined || bruto === null || String(bruto).trim() === "") return;
    if (campo.tipo === "number") {
      const n = Number(String(bruto).replace(/[,\s]/g, ""));
      if (!Number.isFinite(n)) errores.push({ campo: campo.nombre, motivo: "debe ser un número" });
      else camposPersonalizados[campo._id] = n;
    } else if (campo.tipo === "boolean") {
      const b = booleanoExcel(bruto);
      if (!b.ok) errores.push({ campo: campo.nombre, motivo: "use Si o No" });
      else camposPersonalizados[campo._id] = b.valor;
    } else if (campo.tipo === "date") {
      const f = fechaExcel(bruto);
      if (!f.ok) errores.push({ campo: campo.nombre, motivo: "no es una fecha válida (use dd/mm/aaaa)" });
      else camposPersonalizados[campo._id] = f.valor;
    } else if (campo.tipo === "select") {
      const op = (campo.opciones || []).find((o) => normalizar(o) === normalizar(bruto));
      if (!op) errores.push({ campo: campo.nombre, motivo: `"${bruto}" no está en la lista de opciones` });
      else camposPersonalizados[campo._id] = op;
    } else {
      camposPersonalizados[campo._id] = String(bruto).trim();
    }
  });

  if (errores.length) return { ok: false, errores };
  return {
    ok: true,
    datos: {
      numeroFila: (fila["__rowNum__"] ?? 0) + 1,
      nombre,
      apellidoPaterno,
      apellidoMaterno: get("Apellido materno"),
      numero,
      correo,
      descripcion,
      valor,
      origen: get("Origen"),
      usuarioId,
      _vendedorNombre: vendedorNombre,
      camposPersonalizados,
    },
  };
};

const columnasPreview = [
  { title: "Fila", dataIndex: "numeroFila", key: "numeroFila", width: 60 },
  {
    title: "Contacto",
    key: "contacto",
    width: 220,
    render: (_, r) => [r.nombre, r.apellidoPaterno, r.apellidoMaterno].filter(Boolean).join(" "),
  },
  { title: "Teléfono", dataIndex: "numero", key: "numero", width: 120 },
  { title: "Correo", dataIndex: "correo", key: "correo", width: 180 },
  { title: "Descripción", dataIndex: "descripcion", key: "descripcion", width: 240 },
  { title: "Valor", dataIndex: "valor", key: "valor", width: 100 },
  { title: "Origen", dataIndex: "origen", key: "origen", width: 120 },
  { title: "Vendedor", dataIndex: "_vendedorNombre", key: "_vendedorNombre", width: 180 },
];

class ImportarLeadsCRM extends Component {
  state = {
    embudos: [],
    embudoId: null,
    etapaId: null,
    resultado: null,
    destino: null,
    versionArchivo: 0,
  };

  validarFila = construirValidador(() => this.state.embudoId);

  componentDidMount() {
    axios
      .post(
        "leads/datosImportarLeadsCRM",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2) this.setState({ embudos: res.data.embudos || [] });
      })
      .catch((err) => console.log(err.response?.data || err));
  }

  cambiarEmbudo = (embudoId) => {
    this.setState({ embudoId, etapaId: null, versionArchivo: this.state.versionArchivo + 1 });
  };

  antesDeImportar = () => {
    if (!this.state.embudoId || !this.state.etapaId) {
      Modal.warning({ title: "Elija el embudo y la etapa donde entrarán los leads" });
      return false;
    }
    return true;
  };

  render() {
    const { embudos, embudoId, etapaId, resultado } = this.state;
    const embudo = embudos.find((e) => e._id === embudoId);
    const etapa = embudo?.etapas?.find((e) => e._id === etapaId);
    const resultados = resultado?.resultados || [];
    const errores = resultados.filter((r) => r.estado === "error");
    const creados = resultados.filter((r) => r.estado === "creado");
    const contactosNuevos = creados.filter((r) => r.contactoNuevo).length;
    const destino = this.state.destino;
    const mosaico = (numero, titulo, color) => (
      <div
        style={{
          flex: 1,
          minWidth: 120,
          padding: "14px 12px",
          borderRadius: 8,
          background: "var(--ant-background, #fafafa)",
          border: "1px solid rgba(128,128,128,0.2)",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1, color }}>{numero}</div>
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>{titulo}</div>
      </div>
    );

    return (
      <>
        <ImportadorExcelGenerico
          key={this.state.versionArchivo}
          {...this.props}
          titulo="Importar Leads"
          nombreEntidadPlural="leads"
          endpointDatosImportar="leads/datosImportarLeadsCRM"
          endpointImportar="leads/importarLeadsCRM"
          nombrePlantilla={`plantillaLeads${embudo ? "_" + embudo.nombreEmbudo.replace(/[^\w-]+/g, "_") : ""}.xlsx`}
          prepararCatalogos={prepararCatalogos}
          construirPlantilla={(cat) => construirPlantilla(cat, { embudo, etapa })}
          validarFila={this.validarFila}
          columnasPreview={columnasPreview}
          tamanoLote={200}
          antesDeImportar={this.antesDeImportar}
          prepararPayloadExtra={() => ({ embudoId, etapaId })}
          instrucciones={[
            "Descargue la plantilla: la hoja “Leads” trae las columnas, la hoja “Vendedores” lista quién puede recibir leads en cada embudo y, si existen, “Campos personalizados” explica sus valores.",
            "Obligatorios: Nombre, Apellido paterno y Descripción del lead. Cada fila crea su contacto y su lead.",
            "Si el teléfono o el correo ya pertenecen a un contacto, el lead se liga a ese contacto y no se duplica.",
            "Vendedor (opcional): escriba el correo o el nombre completo del usuario. Vacío = queda a su nombre.",
            "Elija el embudo y la etapa antes de subir el archivo; todos los leads entran ahí. Si cambia de embudo debe volver a subir el archivo.",
          ]}
          controlesExtra={
            <div style={{ maxWidth: 620, margin: "10px 0" }}>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <div style={{ fontSize: 12, fontWeight: 500, color: "#4a5866", marginBottom: 4 }}>Embudo</div>
                <SelectItem
                  style={{ width: "100%" }}
                  placeholder="Seleccione un embudo"
                  value={embudoId || undefined}
                  onChange={this.cambiarEmbudo}
                  options={embudos.map((e) => ({ value: e._id, label: e.nombreEmbudo }))}
                />
              </Col>
              <Col xs={24} md={12}>
                <div style={{ fontSize: 12, fontWeight: 500, color: "#4a5866", marginBottom: 4 }}>Etapa</div>
                <SelectItem
                  style={{ width: "100%" }}
                  placeholder="Seleccione una etapa"
                  disabled={!embudo}
                  value={etapaId || undefined}
                  onChange={(v) => this.setState({ etapaId: v })}
                  options={(embudo?.etapas || []).map((e) => ({ value: e._id, label: e.nombre }))}
                />
              </Col>
            </Row>
            <Alert
              style={{ marginTop: 10 }}
              type={embudo ? "info" : "warning"}
              showIcon
              message={
                embudo
                  ? `Los leads entrarán a “${embudo.nombreEmbudo}”${etapa ? ` › ${etapa.nombre}` : ""}. Vendedores válidos: ${embudo.usuarios.length}.`
                  : "Elija el embudo antes de descargar la plantilla: así la hoja Vendedores trae solo a quienes pueden recibir leads en ese embudo."
              }
            />
            </div>
          }
          onImportSuccess={(resData) => {
            this.setState({
              resultado: resData,
              destino: { embudo: embudo?.nombreEmbudo, etapa: etapa?.nombre },
            });
            return true;
          }}
        />
        <Modal
          visible={!!resultado}
          title="Resultado de la importación"
          width={window.innerWidth > 900 ? 640 : "96%"}
          onCancel={() => this.setState({ resultado: null })}
          footer={[
            <ButtonItem key="seguir" blanco onClick={() => this.setState({ resultado: null })}>
              Seguir importando
            </ButtonItem>,
            <ButtonItem key="ir" azul onClick={() => this.props.history.push("/panel/crmDashboard")}>
              Ir al CRM
            </ButtonItem>,
          ]}
        >
          {destino?.embudo && (
            <div style={{ marginBottom: 14, fontSize: 13 }}>
              Destino: <b>{destino.embudo}</b> › <b>{destino.etapa}</b>
            </div>
          )}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            {mosaico(creados.length, "Leads creados", "#389e0d")}
            {mosaico(contactosNuevos, "Contactos nuevos", "#1677ff")}
            {mosaico(errores.length, "Filas rechazadas", errores.length ? "#d46b08" : "inherit")}
          </div>
          {resultado?.incompleto && (
            <Alert type="error" showIcon message={resultado.mensajeError} style={{ marginBottom: 12 }} />
          )}
          {errores.length > 0 && (
            <Table
              size="small"
              rowKey={(r, i) => `${r.numeroFila}-${i}`}
              pagination={{ pageSize: 10 }}
              dataSource={errores}
              columns={[
                { title: "Fila", dataIndex: "numeroFila", width: 60 },
                {
                  title: "Motivo",
                  dataIndex: "motivo",
                  render: (v) => <Tag color="orange" style={{ whiteSpace: "normal" }}>{v}</Tag>,
                },
              ]}
            />
          )}
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ImportarLeadsCRM);
